import { nanoid } from "nanoid";
import type { CohortCriteriaDSL, CriteriaGroup, CriteriaFact } from "./types";
import { QuestionLabelMap } from "../../QuestionBuilder/questionTypes";

/**
 * UI Tree Node types with unique IDs for React keys and drag-and-drop
 */
export type GroupNode = {
  id: string;
  type: "group";
  op: "AND" | "OR" | "NOT";
  negate?: boolean;
  children: (CriterionNode | GroupNode)[];
};

export type CriterionNode = {
  id: string;
  type: "criterion";
  fact: string;
  cmp: string;
  value?: any;
  tolerance?: number;
  critical?: boolean;
  window?: { days?: number; hours?: number };
  questionId?: string;
  source: "study" | "profile" | "registry" | null;
  where?: Record<string, any>;
  negate?: boolean; // row-level NOT
  factDetails?: {
    label: string;
    varname: string;
    questionType: string;
    formName: string;
    studyName: string;
    studyId: string;
    questionTypeLabel: string;
    choices?: { id: string; label: string }[];
    format?: string;
    responseType?: string;
  };
};

export type TreeNode = GroupNode | CriterionNode;

/**
 * Type guards
 */
export function isGroupNode(node: TreeNode): node is GroupNode {
  return node.type === "group";
}

export function isCriterionNode(node: TreeNode): node is CriterionNode {
  return node.type === "criterion";
}

/**
 * Create an empty group node
 */
export function createGroupNode(op: "AND" | "OR" | "NOT" = "AND"): GroupNode {
  return {
    id: nanoid(),
    type: "group",
    op,
    children: [],
  };
}

/**
 * Create an empty criterion node
 */
export function createCriterionNode(): CriterionNode {
  return {
    id: nanoid(),
    type: "criterion",
    fact: "",
    cmp: "",
    value: undefined,
    source: null,
  };
}

/**
 * Convert DSL CriteriaGroup to UI GroupNode (for editing)
 */
export function deserializeGroup(
  group: CriteriaGroup,
  questionMap: Record<string, any>
): GroupNode {
  const node: GroupNode = {
    id: nanoid(),
    type: "group",
    op: group?.op,
    negate: group?.negate,
    children: [],
  };

  node.children = group?.children.map((child) => {
    if ("op" in child && "children" in child) {
      // It's a group
      return deserializeGroup(child as CriteriaGroup, questionMap);
    } else {
      // It's a fact
      const fact = child as CriteriaFact;
      const criterionNode: CriterionNode = {
        id: nanoid(),
        type: "criterion" as const,
        fact: fact.fact,
        cmp: fact.cmp as any, // DSL operator type is compatible
        value: fact.value,
        negate: false, // DSL doesn't have row-level NOT yet
        source: fact.source || null,
      };

      const question = fact.questionId ? questionMap[fact.questionId] : null;

      if (question) {
        criterionNode.factDetails = {
          label: question.label,
          varname: question.varname,
          questionType: question.type || question.questionType,
          formName: question.form?.name ?? "",
          studyName: question?.study?.name || "N/A",
          studyId: question.study?.id ?? "",
          questionTypeLabel:
            QuestionLabelMap[question.type || question.questionType] ||
            "Unknown",
          choices: question.choices
            ? question.choices.sort((a: any, b: any) => a.position - b.position)
            : [],
          format: question.format ?? "",
          responseType: question.responseType ?? "",
        };
      }

      if (fact.tolerance !== undefined)
        criterionNode.tolerance = fact.tolerance;
      if (fact.critical !== undefined) criterionNode.critical = fact.critical;
      if (fact.window) criterionNode.window = fact.window;
      if (fact.questionId) criterionNode.questionId = fact.questionId;
      if (fact.where) criterionNode.where = fact.where;

      return criterionNode;
    }
  });

  return node;
}

/**
 * Convert UI GroupNode back to DSL CriteriaGroup (for saving/execution)
 */
export function serializeGroup(node: GroupNode): CriteriaGroup {
  const group: CriteriaGroup = {
    op: node?.op,
    negate: node?.negate,
    children: [],
  };

  group.children = node.children.map((child) => {
    if (isGroupNode(child)) {
      return serializeGroup(child);
    } else {
      // Convert CriterionNode to CriteriaFact
      const fact: CriteriaFact = {
        fact: child.fact,
        cmp: child.cmp as any, // UI operator type is compatible with DSL
        value: child.value,
        source: child.source || null,
      };

      if (child.tolerance !== undefined) fact.tolerance = child.tolerance;
      if (child.critical !== undefined) fact.critical = child.critical;
      if (child.window) fact.window = child.window;
      if (child.questionId) fact.questionId = child.questionId;
      if (child.where) fact.where = child.where;
      // if (child.factDetails) fact.factDetails = child.factDetails;

      // Handle row-level NOT by wrapping in a NOT group
      if (child.negate) {
        return {
          op: "AND" as const,
          negate: true,
          children: [fact],
        };
      }

      return fact;
    }
  });

  return group;
}

/**
 * Convert complete DSL to UI tree
 */
export function deserializeDSL(
  dsl: CohortCriteriaDSL,
  questions: any[]
): GroupNode {
  const questionMap: Record<string, any> = {};
  questions.forEach((q) => {
    questionMap[q.id] = q;
  });
  return deserializeGroup(dsl.root, questionMap);
}

/**
 * Convert UI tree to complete DSL
 */
export function serializeDSL(
  rootNode: GroupNode,
  options?: {
    studyId?: string;
    scope?: "single-study" | "multi-study";
    output?: {
      dedupeBy: "participant" | "study_participant";
      select: string[];
      orderBy?: { field: string; dir: "asc" | "desc" }[];
      limit?: number;
    };
  }
): CohortCriteriaDSL {
  return {
    scope:
      options?.scope || (options?.studyId ? "single-study" : "multi-study"),
    studyId: options?.studyId,
    root: serializeGroup(rootNode),
    output: options?.output || {
      dedupeBy: "participant",
      select: ["participant_id"],
    },
  };
}

/**
 * Get depth of a tree node
 */
export function getNodeDepth(node: TreeNode): number {
  if (isCriterionNode(node)) {
    return 0;
  }

  let maxDepth = 0;
  for (const child of node.children) {
    maxDepth = Math.max(maxDepth, 1 + getNodeDepth(child));
  }

  return maxDepth;
}

/**
 * Count total criteria in a tree
 */
export function countCriteria(node: TreeNode): number {
  if (isCriterionNode(node)) {
    return 1;
  }

  let count = 0;
  if (!node || !node.children) return count;
  for (const child of node.children) {
    count += countCriteria(child);
  }

  return count;
}

/**
 * Count total groups in a tree
 */
export function countGroups(node: TreeNode): number {
  if (isCriterionNode(node)) {
    return 0;
  }

  let count = 1; // Count the current group

  if (!node || !node.children) return count;
  for (const child of node.children) {
    count += countGroups(child);
  }

  return count;
}

/**
 * Find a node by ID in the tree
 */
export function findNodeById(node: TreeNode, id: string): TreeNode | null {
  if (node.id === id) {
    return node;
  }

  if (isGroupNode(node)) {
    for (const child of node.children) {
      const found = findNodeById(child, id);
      if (found) return found;
    }
  }

  return null;
}

/**
 * Update a node in the tree (returns new tree with immutability)
 */
export function updateNodeById(
  node: TreeNode,
  id: string,
  updates: Partial<GroupNode> | Partial<CriterionNode>
): TreeNode {
  if (node.id === id) {
    return { ...node, ...updates } as TreeNode;
  }

  if (isGroupNode(node)) {
    return {
      ...node,
      children: node.children.map((child) =>
        updateNodeById(child, id, updates)
      ),
    };
  }

  return node;
}

/**
 * Remove a node from the tree
 */
export function removeNodeById(node: GroupNode, id: string): GroupNode {
  return {
    ...node,
    children: node.children
      .filter((child) => child.id !== id)
      .map((child) => (isGroupNode(child) ? removeNodeById(child, id) : child)),
  };
}

/**
 * Add a child to a group node
 */
export function addChildToGroup(
  node: GroupNode,
  groupId: string,
  child: TreeNode
): GroupNode {
  if (node.id === groupId) {
    return {
      ...node,
      children: [...node.children, child],
    };
  }

  return {
    ...node,
    children: node.children.map((c) =>
      isGroupNode(c) ? addChildToGroup(c, groupId, child) : c
    ),
  };
}

/**
 * Validate a tree node
 */
export function validateNode(node: TreeNode): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (isGroupNode(node)) {
    if (!node.op || !["AND", "OR", "NOT"].includes(node.op)) {
      errors.push("Group must have a valid operator (AND, OR, or NOT)");
    }

    if (node.children.length === 0) {
      errors.push("Group cannot be empty");
    }

    if (node.negate && node.children.length === 0) {
      errors.push("NOT group cannot be empty");
    }

    // Recursively validate children
    for (const child of node.children) {
      const childResult = validateNode(child);
      errors.push(...childResult.errors);
    }
  } else {
    if (!node.fact || node.fact.trim() === "") {
      errors.push("Criterion must have a fact/field");
    }

    if (!node.cmp) {
      errors.push("Criterion must have an operator");
    }

    const requiresValue = [
      "=",
      "!=",
      ">",
      ">=",
      "<",
      "<=",
      "between",
      "in",
      "not_in",
      "contains",
    ];
    if (
      requiresValue.includes(node.cmp) &&
      (node.value === undefined || node.value === null)
    ) {
      errors.push(`Operator '${node.cmp}' requires a value`);
    }

    // Validate tolerance if present
    if (node.tolerance !== undefined && node.tolerance !== null) {
      if (node.tolerance < 0) {
        errors.push("Tolerance must be a positive number");
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

export const canSwitchStudy = (
  studyId: string,
  rootNode: GroupNode
): boolean => {
  // Check if any criterion nodes are tied to a specific study
  const hasStudySpecificCriteria = (node: TreeNode): boolean => {
    if (isCriterionNode(node)) {
      return (
        node.factDetails?.studyId !== undefined &&
        node.factDetails?.studyId !== "" &&
        node.factDetails?.studyId !== studyId
      );
    }

    return node.children.some((child) => hasStudySpecificCriteria(child));
  };
  return !hasStudySpecificCriteria(rootNode);
};
